import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { css } from 'react-emotion';
import * as JsSearch from 'js-search';
import Spinner from '@cimpress/react-components/lib/shapes/Spinner';
import { Pagination, TextField, Icon, colors, Tooltip } from '@cimpress/react-components';

import {
  ALL,
  getResourceTypes,
  INDIVIDUAL,
  NONE,
  PRODUCT_NOTIFICATION
} from '../../../constants/subscriptionConstants';
import { actions as subsActions, selectors as subsSelectors } from '../../../reducers/subscriptions';
import { selectors as identitySelectors } from '../../../reducers/identities';
import { getCounterType, getIdentityNameFromId } from '../../../reducers/identities/utils';
import ErrorRobot from '../../common/ErrorRobot';
import AuthorizationFlow from './AuthorizationFlow';
import SubscriptionAccordion from './SubscriptionAccordion';
import SubscriptionModal from './SubscriptionModal';

const paginationCount = 5;

const searchBarStyle = css({
  width: '100%'
});

const linkStyle = css({
  cursor: 'pointer',
  color: '#0088a9'
});

class SubscriptionSettings extends Component {
  state = {
    currentPage: 0,
    searchText: '',
    showConfigureAllModal: false
  };

  componentDidMount() {
    const { fetchAllSubscriptions, notificationType } = this.props;
    const resourceTypes = getResourceTypes(notificationType);
    resourceTypes.forEach(resourceType => {
      fetchAllSubscriptions(resourceType);
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { fetchAllSubscriptions, notificationType, identityType } = this.props;

    if (prevProps.identityType !== identityType || prevProps.notificationType !== notificationType) {
      this.setState({ currentPage: 0 }, () => {
        const resourceTypes = getResourceTypes(notificationType);
        resourceTypes.forEach(resourceType => {
          fetchAllSubscriptions(resourceType);
        });
      });
    }

    if (prevState.searchText !== this.state.searchText) {
      this.setState({ currentPage: 0 });
    }
  }

  onPageChange = ({ selected }) => {
    this.setState({ currentPage: selected });
  };

  onOpenModal = () => {
    this.setState({ showConfigureAllModal: true });
  };

  onCloseModal = () => {
    this.setState({ showConfigureAllModal: false });
  };

  onChangeSearch = event => {
    this.setState({ searchText: event.target.value });
  };

  onSubscribeByRelationship = (id, type, counterPartyIds) => {
    const previousSubs = this.getCurrentSubscriptionIds(id);
    const { subscribe, notificationType } = this.props;

    subscribe(previousSubs, INDIVIDUAL, id, type, notificationType, counterPartyIds);
  };

  onSubscribeByIdentity = (id, type) => {
    const previousSubs = this.getCurrentSubscriptionIds(id);
    const { subscribe, notificationType } = this.props;

    subscribe(previousSubs, ALL, id, type, notificationType);
  };

  onUnsubscribeByIdentity = (id, type) => {
    const previousSubs = this.getCurrentSubscriptionIds(id);
    this.props.unsubscribe(previousSubs, id, type, this.props.notificationType);
  };

  onConfigureIndividual = (id, type, selectedNotification, counterPartyIds) => {
    switch (selectedNotification) {
      case NONE:
        this.onUnsubscribeByIdentity(id, type);
        break;
      case ALL:
        this.onSubscribeByIdentity(id, type);
        break;
      default:
        this.onSubscribeByRelationship(id, type, counterPartyIds);
    }
  };

  createPaginatedAccordions = (searchedIdentities, pageCount, counterParties, hasOnlyOneIdentity, currentPage) => {
    const {
      fulfillersById,
      merchantsById,
      contractSubscriptions,
      productSubscriptions,
      identityType,
      notificationType
    } = this.props;

    const startIndex = paginationCount * currentPage;
    const identitiesToRender = searchedIdentities.slice(
      startIndex,
      currentPage === pageCount ? searchedIdentities.length : startIndex + paginationCount
    );

    return identitiesToRender.map(({ id, name }, idx) => {
      const subscriptions =
        notificationType === PRODUCT_NOTIFICATION ? productSubscriptions[id] : contractSubscriptions[id];
      const subscriptionType = (subscriptions && subscriptions.subscriptionType) || NONE;
      let subscribedParties = [];

      if (subscriptionType === INDIVIDUAL) {
        const counterPartyType = getCounterType(identityType);

        subscribedParties = subscriptions.counterParties.map(({ counterPartyId }) => {
          const counterPartyName = getIdentityNameFromId(
            fulfillersById,
            merchantsById,
            counterPartyId,
            counterPartyType
          );
          return { value: counterPartyId, label: counterPartyName };
        });
      }

      return (
        <SubscriptionAccordion
          key={idx}
          defaultSelectedNotification={subscriptionType}
          counterParties={counterParties}
          identityType={identityType}
          identityId={id}
          identityName={name}
          onSave={this.onConfigureIndividual}
          subscribedParties={subscribedParties}
          open={hasOnlyOneIdentity}
          notificationType={notificationType}
        />
      );
    });
  };

  getCurrentSubscriptionIds = id => {
    const { contractSubscriptions, productSubscriptions, notificationType } = this.props;
    const previousSubs =
      notificationType === PRODUCT_NOTIFICATION ? productSubscriptions[id] : contractSubscriptions[id];
    const subscriptionType = previousSubs && previousSubs.subscriptionType;

    switch (subscriptionType) {
      case ALL:
        return notificationType === PRODUCT_NOTIFICATION
          ? previousSubs.counterParties.reduce(
              (allSubs, counterParty) => allSubs.concat(counterParty.subscriptionIds),
              []
            )
          : [previousSubs.counterParties.subscriptionId];
      case INDIVIDUAL:
        return notificationType === PRODUCT_NOTIFICATION
          ? previousSubs.counterParties.reduce(
              (allSubs, counterParty) => allSubs.concat(counterParty.subscriptionIds),
              []
            )
          : previousSubs.counterParties.map(({ subscriptionId }) => subscriptionId);
      case NONE:
      default:
        return [];
    }
  };

  makeSearchParams = identities => {
    const search = new JsSearch.Search('id');
    search.addIndex('name');
    search.addDocuments(identities);

    return search;
  };

  onConfigureMultipleIdentities = (
    subscriptionType,
    identityType,
    identityIds,
    counterPartyIds,
    allIdentitiesSelected
  ) => {
    const { authorizedIdentities, configureMultipleIdentities, notificationType } = this.props;
    const identitiesToSubscribe = allIdentitiesSelected ? authorizedIdentities.map(({ id }) => id) : identityIds;

    configureMultipleIdentities(
      subscriptionType,
      identityType,
      identitiesToSubscribe,
      notificationType,
      counterPartyIds
    );
  };

  render() {
    const { searchText, currentPage, showConfigureAllModal } = this.state;
    const {
      t,
      authorizedIdentities,
      availableCounterParties,
      isFetchingSubscriptions,
      failedToFetchSubscriptions,
      hasOneIdentityType,
      selectedTypeHasOneIdentity,
      identityType,
      notificationType
    } = this.props;

    // has only 1 fulfiller identity or 1 merchant identity, only 1 altogether
    const hasExactlyOneIdentity = hasOneIdentityType && selectedTypeHasOneIdentity;

    const jsSearch = this.makeSearchParams(authorizedIdentities);
    const searchedIdentities = !searchText || searchText === '' ? authorizedIdentities : jsSearch.search(searchText);
    const pageCount = Math.floor(searchedIdentities.length / paginationCount) + 1;

    const searchField = (
      <TextField
        className={searchBarStyle}
        id="SearchBar"
        type="search"
        value={searchText}
        onChange={this.onChangeSearch}
        label={t('settings:subscriptions.searchBy', { context: identityType })}
      />
    );

    const configureMultipleIdentitiesButton = (
      <Tooltip
        style={{ marginBottom: '15px' }}
        contents={t('settings:subscriptions.addSubscriptionTooltip', { context: identityType })}>
        <span className={linkStyle} onClick={this.onOpenModal}>
          {t('settings:subscriptions.addSubscriptionButton', { context: identityType })}
        </span>
      </Tooltip>
    );

    const accordions = this.createPaginatedAccordions(
      searchedIdentities,
      pageCount,
      availableCounterParties,
      selectedTypeHasOneIdentity,
      currentPage
    );

    const pagination = (
      <div style={{ textAlign: 'center' }}>
        <Pagination
          classname={identityType}
          initialPage={currentPage}
          pageCount={pageCount}
          forcePage={currentPage}
          onPageChange={this.onPageChange}
        />
      </div>
    );

    const configureMultipleIdentitiesModal = (
      <SubscriptionModal
        identityType={identityType}
        show={showConfigureAllModal}
        onClose={this.onCloseModal}
        onConfigureMultipleIdentities={this.onConfigureMultipleIdentities}
        counterParties={availableCounterParties}
        identities={authorizedIdentities.map(i => ({ value: i.id, label: i.name }))}
        notificationType={notificationType}
      />
    );

    const errorPage = <ErrorRobot msg={t('settings:subscriptions.fetchingErrorMessage')} />;

    const loadingComponent = (
      <div className={'loading-container'}>
        <Spinner />
        <h5>{t('settings:subscriptions.fetchingMessage')}</h5>
      </div>
    );

    const body = (
      <div>
        {!selectedTypeHasOneIdentity && (
          <div className="settings__search-header">
            {searchField}
            <span className={'settings__subscriptions-icon'}>
              <Icon name="search" size="lg" color={colors.ocean.base} />
            </span>
            {configureMultipleIdentitiesModal}
          </div>
        )}
        {configureMultipleIdentitiesButton}
        {accordions}
        {!selectedTypeHasOneIdentity && pagination}
      </div>
    );

    const configurationPage = (
      <div>
        <p className="lead" style={{ fontSize: 15 }}>
          {t('settings:subscriptions.header2', { context: notificationType })}
          {!hasExactlyOneIdentity && t('settings:subscriptions.header3')}
        </p>
        {body}
      </div>
    );

    const displayPage = failedToFetchSubscriptions ? errorPage : configurationPage;

    return (
      <div>
        <AuthorizationFlow redirectUri={window.location.href} />
        {isFetchingSubscriptions ? loadingComponent : displayPage}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { identityType } = ownProps;
  return {
    isAuthorized: subsSelectors.getIsAuthorized(state),
    isFetchingSubscriptions: subsSelectors.getIsFetchingAllSubscriptions(state),
    failedToFetchSubscriptions: subsSelectors.getHasFetchingAllSubscriptionsFailed(state),
    fulfillersById: identitySelectors.getFulfillersById(state),
    merchantsById: identitySelectors.getMerchantsById(state),
    authorizedIdentities: identitySelectors.getAuthorizedIdentities(state, identityType),
    availableCounterParties: identitySelectors.getAvailableCounterParties(state, identityType),
    selectedTypeHasOneIdentity: identitySelectors.getSelectedTypeHasOneIdentity(state, identityType),
    hasOneIdentityType: identitySelectors.getHasOneIdentityType(state),
    contractSubscriptions: subsSelectors.getAvailableSubscriptions(state, identityType),
    productSubscriptions: subsSelectors.getGroupedProductSubscriptions(state)
  };
};

const mapDispatchToProps = {
  fetchAllSubscriptions: subsActions.fetchAllSubscriptions,
  subscribe: subsActions.subscribeToNotifications,
  unsubscribe: subsActions.unsubscribeFromNotifications,
  configureMultipleIdentities: subsActions.configureMultipleIdentities
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SubscriptionSettings));
